import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { playerRequest } from '../../service'
import { apiErrorText } from '../../service/coaching'

const timeLabel = value => `${Math.floor(value / 60)}:${(value % 60).toFixed(1).padStart(4, '0')}`

// The preview remains in the browser. Only source-relative metadata is saved.
export function captureReferenceFrame (player) {
  const video = player?.el()?.querySelector('video')
  if (!video || video.readyState < 2 || video.seeking || !video.videoWidth || !Number.isFinite(video.currentTime)) {
    throw new Error('Wait for the video to finish seeking, then try again.')
  }
  player.pause()
  const frame = document.createElement('canvas')
  frame.width = video.videoWidth
  frame.height = video.videoHeight
  frame.getContext('2d').drawImage(video, 0, 0)
  return { frame, timestamp: video.currentTime }
}

export default function PlayerReferences ({ uploadId, capture, initialContext, onClose, onSeek, onSaved }) {
  const canvas = useRef(null)
  const drag = useRef(null)
  const requestId = useRef(crypto.randomUUID())
  const attemptedBody = useRef(null)
  const mounted = useRef(true)
  const [players, setPlayers] = useState([])
  const [data, setData] = useState(null)
  const [selected, setSelected] = useState('')
  const [box, setBox] = useState(null)
  const [confirmed, setConfirmed] = useState(false)
  const [team, setTeam] = useState('')
  const [jersey, setJersey] = useState('')
  const [uniform, setUniform] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)
  const endpoint = `/uploads/${encodeURIComponent(uploadId)}/player-references`

  useEffect(() => {
    mounted.current = true
    Promise.all([playerRequest('/players'), playerRequest(endpoint)]).then(([p, d]) => {
      if (!mounted.current) return
      setPlayers(p); setData(d)
      const player = p.find(row => row.id === initialContext?.playerId) || (p.length === 1 ? p[0] : null)
      if (player) {
        setSelected(player.id); setTeam(initialContext?.team || player.team || '')
        setJersey(initialContext?.jersey || player.jersey || ''); setUniform(initialContext?.uniform || '')
      }
    }).catch(e => { if (mounted.current) setError(e.message) })
    return () => { mounted.current = false }
  }, [endpoint])

  const previewRef = useCallback(node => {
    canvas.current = node
    if (node) {
      node.width = capture.frame.width
      node.height = capture.frame.height
      node.getContext('2d').drawImage(capture.frame, 0, 0)
    }
  }, [capture])

  const point = event => {
    const rect = event.currentTarget.getBoundingClientRect()
    return { x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)), y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)) }
  }
  const move = event => {
    if (!drag.current) return
    const end = point(event)
    setBox({ x: Math.min(drag.current.x, end.x), y: Math.min(drag.current.y, end.y), width: Math.abs(end.x - drag.current.x), height: Math.abs(end.y - drag.current.y) })
  }
  const resetConfirmation = () => { setConfirmed(false); setNotice('') }
  const choosePlayer = id => {
    const p = players.find(row => row.id === id)
    setSelected(id); setTeam(p?.team || ''); setJersey(p?.jersey || ''); setUniform(''); resetConfirmation()
  }
  const save = async () => {
    const fields = { player_id: selected, source_revision: data.source_revision, timestamp_seconds: capture.timestamp, box, frame_width: capture.frame.width, frame_height: capture.frame.height, confirmed, team, jersey, uniform }
    const signature = JSON.stringify(fields)
    if (attemptedBody.current && attemptedBody.current !== signature) requestId.current = crypto.randomUUID()
    attemptedBody.current = signature
    setBusy(true); setError(''); setNotice('')
    try {
      const saved = await playerRequest(endpoint, 'POST', { id: requestId.current, ...fields })
      if (!mounted.current) return
      setData(old => ({ ...old, references: [...old.references.filter(row => row.id !== saved.id), saved] }))
      setNotice('Reference saved for this recording.')
      requestId.current = crypto.randomUUID(); attemptedBody.current = null; setConfirmed(false)
      onSaved?.(saved)
    } catch (e) { if (mounted.current) setError(apiErrorText(e, 'The reference was not saved. Retry in a moment.')) } finally { if (mounted.current) setBusy(false) }
  }
  const remove = async id => {
    setBusy(true); setError(''); setNotice('')
    try {
      await playerRequest(`${endpoint}/${encodeURIComponent(id)}`, 'DELETE')
      if (mounted.current) setData(old => ({ ...old, references: old.references.filter(row => row.id !== id) }))
    } catch (e) { if (mounted.current) setError(apiErrorText(e, 'The reference was not removed. Retry in a moment.')) } finally { if (mounted.current) setBusy(false) }
  }
  const validBox = box && box.width * capture.frame.width >= 2 && box.height * capture.frame.height >= 2
  const adjustBox = event => {
    if (!box || busy) return
    const deltas = { ArrowLeft: [-0.005, 0], ArrowRight: [0.005, 0], ArrowUp: [0, -0.005], ArrowDown: [0, 0.005] }
    const delta = deltas[event.key]
    if (!delta) return
    event.preventDefault(); resetConfirmation()
    if (event.shiftKey) {
      setBox({ ...box, width: Math.max(2 / capture.frame.width, Math.min(1 - box.x, box.width + delta[0])), height: Math.max(2 / capture.frame.height, Math.min(1 - box.y, box.height + delta[1])) })
    } else setBox({ ...box, x: Math.max(0, Math.min(1 - box.width, box.x + delta[0])), y: Math.max(0, Math.min(1 - box.height, box.y + delta[1])) })
  }
  return <Dialog open fullWidth maxWidth="md" onClose={busy ? undefined : onClose} aria-labelledby="player-reference-title">
    <DialogTitle id="player-reference-title">Identify player at {timeLabel(capture.timestamp)}</DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <Typography variant="body2">Choose the player and draw a box around them in this one clear view. The next analysis will reuse this reference throughout the recording.</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {notice && <Alert severity="success">{notice}</Alert>}
        {!data && !error && <Typography>Loading player references…</Typography>}
        {data && !players.length && <Alert severity="info">Create a player on the Players page, then return to identify them here.</Alert>}
        <TextField select label="Player to identify" value={selected} onChange={event => choosePlayer(event.target.value)} disabled={busy || !players.length}>
          {players.map(player => <MenuItem key={player.id} value={player.id}>{player.name}</MenuItem>)}
        </TextField>
        <Box role="group" aria-label="Player selection frame" tabIndex={0} onKeyDown={adjustBox}
          onPointerDown={event => { if (busy) return; event.preventDefault(); drag.current = point(event); event.currentTarget.setPointerCapture(event.pointerId); setBox(null); resetConfirmation() }}
          onPointerMove={move} onPointerUp={event => { move(event); drag.current = null }} onPointerCancel={() => { drag.current = null; setBox(null); resetConfirmation() }}
          sx={{ position: 'relative', lineHeight: 0, touchAction: 'none', cursor: 'crosshair', backgroundColor: '#000', outlineOffset: 3 }}>
          <canvas ref={previewRef} style={{ width: '100%', height: 'auto', display: 'block' }} aria-label="Paused video frame" />
          {box && <Box data-testid="player-reference-box" sx={{ position: 'absolute', pointerEvents: 'none', left: `${box.x * 100}%`, top: `${box.y * 100}%`, width: `${box.width * 100}%`, height: `${box.height * 100}%`, border: '2px solid #ffda44', boxSizing: 'border-box', background: 'rgba(255,218,68,.08)' }} />}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" disabled={busy} onClick={() => { setBox({ x: 0.4, y: 0.3, width: 0.2, height: 0.4 }); resetConfirmation(); canvas.current.parentElement.focus() }}>Start centered box</Button>
          <Button size="small" disabled={busy || !box} onClick={() => { setBox(null); resetConfirmation() }}>Clear box</Button>
        </Stack>
        <Typography variant="caption">Drag with a mouse or finger. Arrow keys move the centered box; Shift + arrows resize it.</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField fullWidth label="Team in this video" value={team} disabled={busy} inputProps={{ maxLength: 200 }} onChange={e => { setTeam(e.target.value); resetConfirmation() }} />
          <TextField label="Jersey in this video" value={jersey} disabled={busy} inputProps={{ maxLength: 3, inputMode: 'numeric' }} onChange={e => { setJersey(e.target.value.replace(/\D/g, '')); resetConfirmation() }} />
          <TextField fullWidth label="Uniform color" value={uniform} disabled={busy} inputProps={{ maxLength: 200 }} onChange={e => { setUniform(e.target.value); resetConfirmation() }} />
        </Stack>
        <FormControlLabel control={<Checkbox checked={confirmed} disabled={!selected || !validBox || busy} onChange={event => setConfirmed(event.target.checked)} />} label="I confirm the selected player is inside this box" />
        <Typography variant="caption">After saving, continue to analysis to match the player across the recording. You do not need to mark every clip.</Typography>
        {data?.references.length > 0 && <Box>
          <Typography variant="subtitle2">Saved references</Typography>
          {data.references.map(row => <Stack key={row.id} direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ py: 0.5 }}>
            <Typography variant="body2" sx={{ flex: 1 }}>{players.find(p => p.id === row.player_id)?.name || 'Player'} · {timeLabel(row.timestamp_seconds)}{row.status === 'source_changed' ? ' · Video changed, recapture needed' : ''}</Typography>
            <Button size="small" disabled={busy || row.status === 'source_changed'} onClick={() => { onClose(); onSeek(row.timestamp_seconds) }}>View reference</Button>
            <Button size="small" color="error" disabled={busy} aria-label={`Remove reference at ${timeLabel(row.timestamp_seconds)}`} onClick={() => remove(row.id)}>Remove</Button>
          </Stack>)}
        </Box>}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={busy}>Close</Button>
      <Button variant="contained" disabled={!data?.can_capture || !selected || !validBox || !confirmed || busy} onClick={save}>{busy ? 'Saving…' : 'Save reference'}</Button>
    </DialogActions>
  </Dialog>
}

PlayerReferences.propTypes = { uploadId: PropTypes.string.isRequired, capture: PropTypes.object.isRequired, initialContext: PropTypes.object, onSaved: PropTypes.func, onClose: PropTypes.func.isRequired, onSeek: PropTypes.func.isRequired }
